import { UserOutlined, LogoutOutlined } from '@ant-design/icons'
import { Layout as AntLayout, Breadcrumb, Menu, Dropdown } from 'antd'
import { Link, navigate } from 'gatsby'
import type { ReactElement } from 'react'
import React, { ReactNode } from 'react'
import { Helmet } from 'react-helmet'
import { useAuth } from '../data/contexts/auth'
import { getDebugLogger } from '../utils/logger'
import { BillingButton } from './account/billing-button'
import { logout } from '../data/api-adapters/auth'

// Assets and styles
import 'antd/dist/antd.less'
import logo from '../images/logo.svg'
import * as styles from './layout.module.css'

const APP_TITLE_SUFFIX = 'Advisor Platinum'
export const PAGE_HOME = 'home_page'
export const PAGE_CLIENTS = 'clients_page'
export const PAGE_ADVISOR_ALLOCATIONS = 'advisor_allocations_page'
export const PAGE_CLIENT = 'client_page'
export const PAGE_FACT_FINDER = 'fact_finder_page'
export const PAGE_CLIENT_ALLOCATIONS = 'client_allocations_page'
export const PAGE_NEW_CALC = 'new_calc_page'
export const PAGE_SAVED_CALC = 'saved_calc_page'
export const PAGE_RANDOM_RETURNS = 'random_returns_page'

const DEBUG_PREFIX = 'Layout'

const { Header, Content, Footer, Sider } = AntLayout
const debug = getDebugLogger(DEBUG_PREFIX)

export function getTitleSuffixed(title) {
  return (title ? title + ' · ' : '') + APP_TITLE_SUFFIX
}

export function Layout({
  title,
  pageId,
  breadcrumbs,
  breadcrumbButtons,
  leftSider,
  rightSider,
  children,
}: {
  pageId: string,
  title?: string,
  breadcrumbs?: Array<ReactNode>,
  breadcrumbButtons?: ReactNode,
  leftSider?: ReactNode,
  rightSider?: ReactNode,
  children?: ReactNode,
}) {
  const auth = useAuth()

  debug('Layout', { auth })
  const userdata = auth.authData.userdata

  let breadcrumbNodes: Array<ReactNode> | null = null
  if (breadcrumbs) {
    breadcrumbNodes = breadcrumbs.map((item, i) =>
      <Breadcrumb.Item key={i+1}>{item}</Breadcrumb.Item>
    )
  }

  let printElement: ReactElement | null = null
  if (breadcrumbButtons != null) {
    printElement = (
      <div className={styles.printPositionRelative}>
        <div className={styles.printContainer}>
          {breadcrumbButtons}
        </div>
      </div>
    )
  }

  return (
    <AntLayout className={styles.container}>
      <Helmet>
        <meta name='viewport' content='width=device-width, initial-scale=1.0' />
        <title>{ getTitleSuffixed(title) }</title>
      </Helmet>
      <Header className={styles.header}>
        <div className={styles.logo}><Link to='/clients'>
          <img className={styles.logoImg} src={logo} alt=''/></Link>
        </div>
        <Menu
          theme='dark'
          mode='horizontal'
          defaultSelectedKeys={[pageId]}
          items={[
            {
              key: PAGE_CLIENTS,
              label: <Link to='/clients'>Clients</Link>
            },
            {
              key: PAGE_ADVISOR_ALLOCATIONS,
              label: <Link to='/advisor-allocations'>Advisor Allocations</Link>
            }
          ]}
        />
        <div className={styles.usernameBox}>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'logout',
                  label: 'Logout',
                  icon: <LogoutOutlined />,
                  onClick: () => {
                    debug('Logout clicked, calling logout function')
                    logout()
                  }
                }
              ]
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <div className={styles.userMenuTrigger}>
              <span className={styles.usernameParagraph}>{ userdata?.name }</span>
              <UserOutlined className={styles.userIcon} />
            </div>
          </Dropdown>
          {(userdata?.stripeEnabled) && (
            <div className={styles.usernameBilling}>
              <BillingButton />
            </div>
          )}
        </div>
      </Header>
      <AntLayout>
        {leftSider}
        <Content style={{ padding: '0 50px' }}>
          <Breadcrumb style={{ margin: '16px 0', marginBottom: 7 }}>
            {breadcrumbNodes}
          </Breadcrumb>
          {printElement}
          <div className={styles.content}>
            {children}
          </div>
        </Content>
        {rightSider}
      </AntLayout>
      <Footer style={{ textAlign: 'center' }}>
        <span>2025</span>
      </Footer>
    </AntLayout>
  )
}
